'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import { ChevronDown, ArrowRight, Search, ChevronRight, ChevronLeft } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useClickOutside } from '@/hooks/useClickOutside';
import type { Deporte, SubDeporte } from '../../types/category';
import { createSportSlug, createCategorySlug } from '@/utils/slugs';

// Import all locale files
import commonEs from '@/locales/es/common.json';
import commonEn from '@/locales/en/common.json';
import commonCa from '@/locales/ca/common.json';
import commonEu from '@/locales/eu/common.json';
import commonFr from '@/locales/fr/common.json';
import commonIt from '@/locales/it/common.json';
import commonPt from '@/locales/pt/common.json';
import { useCachedSports } from '@/hooks/useCachedSports';

interface CategoryNavProps {
  className?: string;
}

// IDs of the featured sports to show in the navbar
// Running, Cycling, Triathlon, Trail
const FEATURED_SPORT_IDS = [7, 21, 99, 104];

// Helper function to transform sportsTranslations to Deporte[]
const transformSportsTranslations = (translations: any): Deporte[] => {
  if (!translations?.sportsTranslations?.names) {
    return [];
  }

  const { names, subsports } = translations.sportsTranslations;
  const sports: Deporte[] = [];

  // Convert names object to array of Deporte
  Object.entries(names).forEach(([idStr, nombre]) => {
    const idDeporte = parseInt(idStr);
    const subDeportesData = subsports?.[idStr] || {};

    const subDeportes: SubDeporte[] = Object.entries(subDeportesData).map(([subIdStr, subNombre]) => ({
      idSubDeporte: parseInt(subIdStr),
      idDeporte,
      nombre: subNombre as string
    }));

    sports.push({
      idDeporte,
      nombre: nombre as string,
      subDeportes
    });
  });

  // Sort by name
  return sports.sort((a, b) => a.nombre.localeCompare(b.nombre));
};

// Get translations for a specific language
const getTranslationsForLang = (lang: string) => {
  switch (lang) {
    case 'es':
      return commonEs;
    case 'en':
      return commonEn;
    case 'ca':
      return commonCa;
    case 'eu':
      return commonEu;
    case 'fr':
      return commonFr;
    case 'it':
      return commonIt;
    case 'pt':
      return commonPt;
    default:
      return commonEn;
  }
};

export default function CategoryNav({ className = '' }: CategoryNavProps) {
  const { t, lang } = useTranslation();
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [dropdownMode, setDropdownMode] = useState<'categories' | 'search'>('categories');
  const [selectedDeporte, setSelectedDeporte] = useState<Deporte | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const cachedSports = useCachedSports();

  // Get sports from locale files
  const sports = useMemo(() => {
    const translations = getTranslationsForLang(lang);
	const translatedSports = transformSportsTranslations(translations);
	if (cachedSports === null) {
		return translatedSports;
	}
	return translatedSports.filter(sports => cachedSports.some(cachedSport => cachedSport.idDeporte === sports.idDeporte));
  }, [lang, cachedSports]);

  const sportsEnglish = useMemo(() => {
	const translatedSports = transformSportsTranslations(commonEn);
	if (cachedSports === null) {
		return translatedSports;
	}
    return translatedSports.filter(sports => cachedSports.some(cachedSport => cachedSport.idDeporte === sports.idDeporte));
  }, [cachedSports]);

  // Get featured sports in the correct order
  const featuredSports = useMemo(() => {
    return FEATURED_SPORT_IDS.map(id => sports.find(sport => sport.idDeporte === id)).filter(Boolean) as Deporte[];
  }, [sports]);

  const featuredSportsEnglish = useMemo(() => {
    return FEATURED_SPORT_IDS.map(id => sportsEnglish.find(sport => sport.idDeporte === id)).filter(Boolean) as Deporte[];
  }, [sportsEnglish]);

  useClickOutside(menuRef, (event) => {
    // Don't close if clicking on the toggle button
    if (buttonRef.current?.contains(event.target as Node)) {
      return;
    }
    setIsCategoriesOpen(false);
  });
  useClickOutside(searchRef, () => { });

  const toggleMenu = (mode: 'categories' | 'search', isOpen = true, keepSearchTerm = false) => {
    setDropdownMode(mode);
    setIsCategoriesOpen(isOpen);

    if (!isOpen) {
      setSearchTerm('');
      setSelectedDeporte(null);
    } else if (mode === 'search' && !keepSearchTerm) {
      setSearchTerm('');
      setSelectedDeporte(null);
    }
  };

  const openCategory = (sport: Deporte, direct = false) => {
    // If direct is true, navigate to the sport page directly
    if (direct) {
      const englishSportName = sportsEnglish.find(sportEn => sportEn.idDeporte === sport.idDeporte)?.nombre || sport.nombre;
      window.location.href = `/${lang}/sport/${createSportSlug(englishSportName)}`;
      return;
    }
    // Otherwise, open the search panel with the sport name
    setSearchTerm(sport.nombre);
    setSelectedDeporte(sport);
    toggleMenu('search', true, true);
  };

  const closeAllMenus = () => {
    setIsCategoriesOpen(false);
  };

  const filteredDeportes = searchTerm
    ? sports.filter(deporte =>
      deporte.nombre.toLowerCase().includes(searchTerm.toLowerCase()))
    : sports;

  const filteredSubdeportes = selectedDeporte
    ? selectedDeporte.subDeportes || []
    : [];

  // Efecto para controlar el scroll del fondo cuando el popup está abierto
  useEffect(() => {
    if (isCategoriesOpen) {
      // Prevenir scroll del body
      document.body.style.overflow = 'hidden';
    } else {
      // Restaurar scroll del body
      document.body.style.overflow = 'unset';
    }

    // Cleanup function para cuando el componente se desmonte
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCategoriesOpen]);

  useEffect(() => {
    if (selectedDeporte && dropdownMode === 'search' && isCategoriesOpen) {
      const firstLetter = selectedDeporte.nombre.charAt(0).toUpperCase();

      setTimeout(() => {
        const letterElement = document.getElementById(`letter-${firstLetter}`);
        const sportElement = document.getElementById(`sport-${selectedDeporte.idDeporte}`);
        const container = document.getElementById('sports-scroll-container');

        if (letterElement && container) {
          container.scrollTop = letterElement.offsetTop - container.offsetTop - 20;
        }

        if (sportElement) {
          sportElement.classList.add('bg-primary/10', 'rounded');
        }
      }, 100);
    }
  }, [selectedDeporte, dropdownMode, isCategoriesOpen]);

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center justify-center space-x-5 py-2 overflow-x-auto">
        <div className="flex items-center space-x-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-foreground">
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
            <line x1="3" x2="21" y1="9" y2="9" />
            <line x1="3" x2="21" y1="15" y2="15" />
            <line x1="9" x2="9" y1="3" y2="21" />
            <line x1="15" x2="15" y1="3" y2="21" />
          </svg>
          <button
            ref={buttonRef}
            className="flex items-center space-x-1 text-[14px] text-foreground hover:text-primary transition-colors whitespace-nowrap font-medium"
            onClick={() => toggleMenu('categories', !isCategoriesOpen)}
            aria-expanded={isCategoriesOpen}
          >
            <span>{t('nav.all_categories')}</span>
            <ChevronDown size={14} />
          </button>
        </div>

        {featuredSports.map((sport, index) => (
          <button
            key={sport.idDeporte}
            className="hidden sm:block text-[14px] text-foreground hover:text-primary transition-colors whitespace-nowrap"
            onClick={() => openCategory(featuredSportsEnglish[index], true)}
          >
            {sport.nombre}
          </button>
        ))}
      </div>

      {isCategoriesOpen && (
        <>
          {/* Overlay para móvil */}
          <div className="fixed sm:hidden inset-0 bg-black/50 z-40" onClick={() => setIsCategoriesOpen(false)} />
          
          {/* Modal de categorías */}
          <div
            ref={menuRef}
            className="fixed sm:absolute left-0 right-0 top-[150px] sm:top-full mt-0 z-50 w-full bg-background border border-border shadow-lg dark:shadow-gray-900/30 h-[calc(100vh-150px)] sm:h-auto sm:max-h-none overflow-y-auto sm:overflow-visible"
          >
          {dropdownMode === 'categories' ? (
            <>
              <div className="flex flex-col items-center overflow-y-auto scrollContainer sm:max-h-[50vh]">
                <div className='flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 py-6 px-10 max-w-none md:w-full gap-4'>
                  {featuredSports.map((deporte, index) => (
                    <div key={deporte.idDeporte} className="space-y-2">
                      <h3 className="font-medium text-base flex items-center text-foreground">
                        {deporte.nombre}
                      </h3>
                      <ul className="space-y-1 text-[14px]">
                        {deporte.subDeportes && deporte.subDeportes.length > 0 ? (
                          <>
                            {deporte.subDeportes.slice(0, 4).map((subDeporte) => {
                              const englishSport = featuredSportsEnglish[index];
                              const englishSubSport = englishSport?.subDeportes.find(sub => sub.idSubDeporte === subDeporte.idSubDeporte);
                              const sportSlug = createSportSlug(englishSport?.nombre || '');
                              const categorySlug = createCategorySlug(englishSubSport?.nombre || '');
                              const href = `/${lang}/sport/${sportSlug}/categories/${categorySlug}`;

                              return (
                                <li key={subDeporte.idSubDeporte}>
                                  <Link
                                    href={href}
                                    prefetch={true}
                                    className="text-muted-foreground hover:text-primary transition-colors block py-0.5"
                                    onClick={closeAllMenus}
                                  >
                                    {subDeporte.nombre}
                                  </Link>
                                </li>
                              );
                            })}
                            {deporte.subDeportes.length > 4 && (
                              <li>
                                <button
                                  className="font-medium text-foreground hover:text-primary transition-colors block py-0.5 text-left w-full"
                                  onClick={() => openCategory(deporte)}
                                >
                                  {t('nav.more')}
                                </button>
                              </li>
                            )}
                          </>
                        ) : (
                          <li>
                            <button
                              className="font-medium text-foreground hover:text-primary transition-colors block py-0.5 text-left w-full"
                              onClick={() => openCategory(deporte)}
                            >
                              {t('nav.more')}
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-border p-3">
                <button
                  className="w-full flex items-center justify-center gap-2 py-1.5 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  onClick={() => toggleMenu('search')}
                >
                  <span>{t('nav.view_all_categories')}</span>
                  <ArrowRight size={14} />
                </button>
              </div>
              <div className="h-1"></div>
            </>
          ) : (
            <div className="bg-background w-full dark:bg-background h-full flex flex-col">
              <div className="flex justify-end px-4 pt-2">
                <button
                  className="text-muted-foreground hover:text-primary text-xl"
                  onClick={closeAllMenus}
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-none md:max-w-7xl md:mx-auto px-4 md:px-10 pb-4 sm:pb-10 flex-1 overflow-hidden min-h-0">
                  <div className={`${selectedDeporte ? 'hidden' : ''} lg:block flex flex-col h-full min-h-0`}>
                    <h3 className="text-base sm:text-lg font-semibold mb-3 text-foreground">{t('nav.sports')}</h3>

                    <div className="relative mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                        <input
                          type="text"
                          placeholder={t('search.placeholder')}
                          className="w-full pl-10 pr-4 py-2 border border-input rounded-md 
                            bg-secondary dark:bg-secondary 
                            text-foreground 
                            focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary
                            transition-colors"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    <div id="sports-scroll-container" className="flex-1 overflow-y-auto pr-4 border-r border-border md:h-[300px]">
                      <ul className="space-y-0.5 text-[14px]">
                        {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => {
                          const deportesByLetter = filteredDeportes.filter(
                            (deporte) => deporte.nombre.charAt(0).toUpperCase() === letter
                          );

                          if (deportesByLetter.length === 0) return null;

                          return (
                            <li key={letter} className="py-0.5">
                              <h4 id={`letter-${letter}`} className="font-medium text-[15px] text-foreground py-1">{letter}</h4>
                              <ul className="mt-0.5 space-y-0.5 ml-1">
                                {deportesByLetter.map((deporte) => (
                                  <li key={deporte.idDeporte}>
                                    {deporte.subDeportes && deporte.subDeportes.length > 0 ? (
                                      <button
                                        className={`w-full text-left px-2 py-1 rounded transition-colors flex justify-between items-center ${selectedDeporte?.idDeporte === deporte.idDeporte
                                          ? 'text-primary font-medium bg-primary/10 dark:bg-primary/20'
                                          : 'text-foreground hover:bg-accent/60 dark:hover:bg-accent/30'
                                          }`}
                                        onClick={() => setSelectedDeporte(deporte)}
                                        id={`sport-${deporte.idDeporte}`}
                                      >
                                        <span>{deporte.nombre}</span>
                                        <ChevronRight size={14} />
                                      </button>
                                    ) : (
                                      <Link
                                        href={`/${lang}/sport/${createSportSlug(sportsEnglish.find(sport => sport.idDeporte === deporte.idDeporte)?.nombre || '')}`}
                                        prefetch={true}
                                        className="w-full text-left px-2 py-1 rounded hover:bg-accent/60 dark:hover:bg-accent/30 transition-colors block text-foreground"
                                        onClick={closeAllMenus}
                                      >
                                        {deporte.nombre}
                                      </Link>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>

                  {selectedDeporte && (
                    <div className="pl-2 flex flex-col h-full min-h-0">
                      <div className='flex flex-row items-center gap-2'>
                        <button
                          className="text-muted-foreground hover:text-primary text-xl mb-3 p-1"
                          onClick={() => setSelectedDeporte(null)}
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <h3 className="text-base sm:text-lg font-semibold mb-3 text-foreground">{selectedDeporte.nombre}</h3>
                      </div>

                      {filteredSubdeportes.length > 0 ? (
                        <div className="grid grid-cols-1 gap-y-0.5 flex-1 overflow-y-auto md:max-h-[356px]">
                          {filteredSubdeportes.map((subDeporte) => {
                            const englishSport = sportsEnglish.find(sport => sport.idDeporte === selectedDeporte.idDeporte);
                            const englishSubSport = englishSport?.subDeportes.find(sub => sub.idSubDeporte === subDeporte.idSubDeporte);
                            const sportSlug = createSportSlug(englishSport?.nombre || '');
                            const categorySlug = createCategorySlug(englishSubSport?.nombre || '');
                            const href = `/${lang}/sport/${sportSlug}/categories/${categorySlug}`;

                            return (
                              <Link
                                key={subDeporte.idSubDeporte}
                                href={href}
                                prefetch={true}
                                className="text-foreground hover:text-primary transition-colors py-1.5 block text-[14px]"
                                onClick={closeAllMenus}
                              >
                                {subDeporte.nombre}
                              </Link>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">
                          {t('nav.more')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
            </div>
          )}
          </div>
        </>
      )}
    </div>
  );
} 